import { Box, Divider, Typography } from '@mui/material'
import { useMemo } from 'react'
import { propertyFormConfig } from 'objects/property-forms/PropertyFormConfig.tsx'
import { useOptionsPopover } from 'core/popover/popover-hooks.tsx'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { DefaultPopover } from 'core/popover/DefaultPopover.tsx'
import { Select1PropertyValuePopover } from 'objects/property-forms/select-1/Select1PropertyValuePopover.tsx'
import { isClientId } from '@/common/network/gql-types.ts'
import { globalCssVars } from '@/core/theme/GlobalCssVars.ts'
import type { PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyOptionModel } from '@/generated/graphql.ts'
import { ClickableArea } from '@/objects/property-forms/ClickableArea.tsx'
import { useDiRef } from '@/common/react/di-hooks.ts'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'
import type { PropertyInputProps } from '@/objects/property-forms/PropertyInputProps.tsx'
export interface Select1PropertyInputProps extends PropertyInputProps<number | null> {
  propDef: PropertyDefinitionModel
}

const config = propertyFormConfig

export function Select1PropertyInput({ propDef, value, onValueChange }: Select1PropertyInputProps) {
  const typesService = useDiRef<ObjectTypesService>('ObjectTypesService')
  const popover = useOptionsPopover<PropertyOptionModel | null>()
  const selectedOption = useMemo(() => {
    return propDef.options!.find((x) => x.id === value) ?? { value: 'Not set', color: '#BDBDBD' }
  }, [value, propDef])
  const isPopoverOpen = !!popover.anchorEl

  function onOptionSelected(option: PropertyOptionModel | null) {
    if (option === null) {
      if (value !== null) {
        onValueChange(null)
      }
      return
    }
    if (option.id === value) return
    if (isClientId(option.id)) {
      void typesService.updatePropertyDef(propDef, {
        options: { locallyCreatedOptions: [option] },
      })
    }
    onValueChange(option.id)
  }

  return (
    <>
      <ClickableArea onClick={(e) => popover.openPopover(e.currentTarget, onOptionSelected)}>
        <Box
          sx={{
            minWidth: '100px',
            maxWidth: '200px',
            height: config.select1Height,
            display: 'inline-flex',
            justifyContent: 'space-between',
            alignSelf: 'stretch',
            backgroundColor: selectedOption.color,
            borderRadius: globalCssVars.radius3,
            cursor: 'pointer',
          }}
        >
          <Typography noWrap sx={{ ml: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedOption.value}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Divider orientation="vertical" flexItem sx={{ borderRightWidth: '2px' }} />
            {isPopoverOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </Box>
        </Box>
      </ClickableArea>

      <DefaultPopover
        anchorEl={popover.anchorEl}
        close={popover.closePopover}
        slotSx={{ paper: { p: 0, boxShadow: 3, borderRadius: '10px' } }}
      >
        <Select1PropertyValuePopover propDef={propDef} setPopoverResult={popover.setResult} />
      </DefaultPopover>
    </>
  )
}
